import { useState, useEffect, useCallback, useMemo } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { StatusBadge } from '@/components/StatusBadge';
import { apiService } from '@/services/apiService';
import { GripVertical, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
  rectIntersection,
} from '@dnd-kit/core';

const COLUMNS = [
  { id: 'planejado', label: 'Planejado', color: '#6b7280', bgGradient: 'from-zinc-500/10 to-zinc-600/5' },
  { id: 'aprovado', label: 'Aprovado', color: '#3b82f6', bgGradient: 'from-blue-500/10 to-blue-600/5' },
  { id: 'concluido', label: 'Concluído', color: '#10b981', bgGradient: 'from-emerald-500/10 to-emerald-600/5' },
];

function DraggableTaskCard({ task, onClick, isSelected, onSelect, selectedCount }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging,
  } = useDraggable({ id: task.id });

  const style = transform ? {
    transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
  } : undefined;

  const priorityConfig = {
    1: { color: 'text-red-400', bg: 'bg-red-500/10', label: 'P1' },
    2: { color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'P2' },
    3: { color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'P3' },
    4: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'P4' },
    5: { color: 'text-zinc-400', bg: 'bg-zinc-500/10', label: 'P5' },
  };
  
  const priority = priorityConfig[task.priority] || priorityConfig[3];

  // Click handler: 1º clique seleciona, 2º clique (se já selecionado) abre modal
  const handleCardClick = (e) => {
    e.stopPropagation();
    if (isSelected) {
      // Já selecionado → abre modal
      onClick(task);
    } else {
      // Não selecionado → seleciona
      onSelect(task.id, e.ctrlKey || e.metaKey || e.shiftKey);
    }
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      onClick={handleCardClick}
      className={cn(
        'group relative rounded-lg border bg-gradient-to-br from-card/95 to-card/70 backdrop-blur-sm p-3.5 transition-all duration-200 cursor-pointer',
        isDragging 
          ? 'opacity-30 scale-95 border-primary/30' 
          : isSelected
            ? 'border-primary/70 ring-2 ring-primary/30 shadow-lg shadow-primary/10'
            : 'border-border/30 hover:border-primary/50 hover:shadow-lg hover:shadow-primary/5 hover:translate-y-[-1px]'
      )}
      data-testid="task-card"
    >
      {/* Selection indicator + badge for multi-drag */}
      {isSelected && selectedCount > 1 && isDragging && (
        <div className="absolute -top-2 -right-2 bg-primary text-primary-foreground text-xs font-bold rounded-full h-6 w-6 flex items-center justify-center shadow-lg z-10">
          {selectedCount}
        </div>
      )}
      
      <div className="flex items-start gap-2.5">
        {/* Drag Handle */}
        <div
          className="mt-0.5 cursor-grab active:cursor-grabbing flex-shrink-0 p-1 rounded-md hover:bg-muted/50 transition-colors touch-none opacity-40 group-hover:opacity-70"
          {...attributes}
          {...listeners}
          onClick={(e) => e.stopPropagation()}
          data-testid="task-drag-handle"
        >
          <GripVertical className="h-4 w-4 text-muted-foreground" />
        </div>
        
        {/* Content */}
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold leading-snug line-clamp-2 mb-2 text-foreground/90 group-hover:text-foreground transition-colors">
            {task.title}
          </h4>
          {task.desc && (
            <p className="text-xs text-muted-foreground/70 line-clamp-2 mb-2.5 leading-relaxed">{task.desc}</p>
          )}
          
          {/* Footer */}
          <div className="flex items-center justify-between pt-2 border-t border-border/20">
            <div className="flex items-center gap-2">
              {task.risk_level && (
                <StatusBadge status={task.risk_level} className="text-[9px]" />
              )}
              <div className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded', priority.bg, priority.color)}>
                {priority.label}
              </div>
            </div>
            {task.owner_agent && (
              <span className="text-[10px] text-muted-foreground/60 uppercase font-mono tracking-wider">
                {task.owner_agent}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function OverlayTaskCard({ task, selectedCount }) {
  const priorityConfig = {
    1: { color: 'text-red-400', bg: 'bg-red-500/10', label: 'P1' },
    2: { color: 'text-amber-400', bg: 'bg-amber-500/10', label: 'P2' },
    3: { color: 'text-blue-400', bg: 'bg-blue-500/10', label: 'P3' },
    4: { color: 'text-emerald-400', bg: 'bg-emerald-500/10', label: 'P4' },
    5: { color: 'text-zinc-400', bg: 'bg-zinc-500/10', label: 'P5' },
  };
  
  const priority = priorityConfig[task.priority] || priorityConfig[3];

  return (
    <div className="relative rounded-lg border border-primary/50 bg-gradient-to-br from-card to-card/90 backdrop-blur-md p-3.5 shadow-2xl shadow-primary/20 rotate-2 scale-105 cursor-grabbing w-[320px]">
      {/* Badge mostrando quantidade quando multi-select */}
      {selectedCount > 1 && (
        <div className="absolute -top-3 -right-3 bg-primary text-primary-foreground text-xs font-bold rounded-full h-7 w-7 flex items-center justify-center shadow-lg border-2 border-background z-10">
          {selectedCount}
        </div>
      )}
      <div className="flex items-start gap-2.5">
        <div className="mt-0.5 flex-shrink-0 p-1">
          <GripVertical className="h-4 w-4 text-primary/70" />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-semibold leading-snug line-clamp-2 mb-2 text-foreground">
            {task.title}
          </h4>
          {selectedCount > 1 && (
            <p className="text-xs text-primary/80 mb-2">
              +{selectedCount - 1} tarefa{selectedCount > 2 ? 's' : ''} selecionada{selectedCount > 2 ? 's' : ''}
            </p>
          )}
          <div className="flex items-center justify-between pt-2 border-t border-border/20">
            <div className="flex items-center gap-2">
              {task.risk_level && (
                <StatusBadge status={task.risk_level} className="text-[9px]" />
              )}
              <div className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded', priority.bg, priority.color)}>
                {priority.label}
              </div>
            </div>
            {task.owner_agent && (
              <span className="text-[10px] text-muted-foreground uppercase font-mono tracking-wider">
                {task.owner_agent}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function DroppableColumn({ column, tasks, onTaskClick, isOver, selectedTaskIds, onSelectTask }) {
  const { setNodeRef } = useDroppable({ id: column.id });
  const selectedCount = selectedTaskIds.size;

  return (
    <div className="w-full flex flex-col" data-testid={`kanban-column-${column.id}`}>
      <div className={cn(
        'flex-1 flex flex-col rounded-xl border bg-gradient-to-b from-card/40 to-card/20 backdrop-blur-md overflow-hidden transition-all duration-300',
        isOver ? 'border-primary/60 ring-2 ring-primary/30 shadow-xl shadow-primary/10' : 'border-border/40 shadow-lg'
      )}>
        {/* Column Header */}
        <div
          className={cn(
            'relative p-4 border-b border-border/30',
            'bg-gradient-to-br',
            column.bgGradient
          )}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div 
                className="w-2 h-2 rounded-full animate-pulse"
                style={{ backgroundColor: column.color }}
              />
              <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-foreground/90">
                {column.label}
              </h3>
            </div>
            <div
              className="flex items-center justify-center h-7 min-w-[28px] px-2 rounded-md text-xs font-bold font-mono bg-background/60 backdrop-blur-sm border border-border/40"
              style={{ color: column.color }}
            >
              {tasks.length}
            </div>
          </div>
        </div>

        {/* Scrollable Tasks Container - Full Height */}
        <div
          ref={setNodeRef}
          className={cn(
            'flex-1 p-3 space-y-2.5 overflow-y-auto transition-colors duration-200',
            'scrollbar-thin scrollbar-track-transparent scrollbar-thumb-border/50 hover:scrollbar-thumb-border/70',
            isOver && 'bg-primary/5'
          )}
          style={{ 
            minHeight: '300px',
          }}
          data-testid={`kanban-column-${column.id}-tasks`}
        >
          {tasks.map((task) => (
            <DraggableTaskCard 
              key={task.id} 
              task={task} 
              onClick={onTaskClick}
              isSelected={selectedTaskIds.has(task.id)}
              onSelect={onSelectTask}
              selectedCount={selectedCount}
            />
          ))}
          {tasks.length === 0 && !isOver && (
            <div className="flex flex-col items-center justify-center h-40 text-xs text-muted-foreground/30 border border-dashed border-border/20 rounded-lg bg-muted/5">
              <span className="font-medium">Sem tarefas</span>
              <span className="text-[10px] mt-1">Arraste tarefas aqui</span>
            </div>
          )}
          {isOver && tasks.length === 0 && (
            <div className="flex flex-col items-center justify-center h-40 text-xs text-primary/70 border-2 border-dashed border-primary/40 rounded-lg bg-primary/10 animate-pulse">
              <span className="font-semibold">Solte aqui</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function TasksPage() {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedTask, setSelectedTask] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [overColumnId, setOverColumnId] = useState(null);
  // Multi-select state
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());
  const [formData, setFormData] = useState({
    title: '',
    desc: '',
    status: 'planejado',
    priority: 3,
    risk_level: 'medium',
    owner_agent: 'linus',
    project_id: null,
  });

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    })
  );

  // Mover filteredTasks para cima usando useMemo (antes dos callbacks que dependem dele)
  const filteredTasks = useMemo(() => {
    // Sempre filtra por projeto - não mostra todas as tarefas juntas
    if (!selectedProject) return [];
    return tasks
      .filter(t => t.project_id === selectedProject)
      .sort((a, b) => a.id - b.id); // Ordenar por ID (ordem de criação)
  }, [tasks, selectedProject]);

  useEffect(() => {
    loadTasks();
    loadProjects();
  }, []);

  // Seleciona primeiro projeto automaticamente quando carrega
  useEffect(() => {
    if (projects.length > 0 && !selectedProject) {
      setSelectedProject(projects[0].id);
    }
  }, [projects, selectedProject]);

  const loadTasks = async () => {
    try {
      const data = await apiService.getTasks();
      // Usar status direto do backend (backlog, todo, doing, done)
      setTasks(data);
    } finally {
      setLoading(false);
    }
  };

  const loadProjects = async () => {
    try {
      const data = await apiService.getProjects();
      // Incluir todos projetos não concluídos (ativo, producao, em_andamento, planejamento)
      const activeProjects = data.filter(p => 
        !['concluido', 'cancelado', 'arquivado'].includes(p.status)
      );
      setProjects(activeProjects);
    } catch (error) {
      console.error('Erro ao carregar projetos:', error);
    }
  };

  const handleDragStart = useCallback((event) => {
    setActiveId(event.active.id);
    // Se a tarefa arrastada não está selecionada, limpa seleção e seleciona só ela
    if (!selectedTaskIds.has(event.active.id)) {
      setSelectedTaskIds(new Set([event.active.id]));
    }
  }, [selectedTaskIds]);

  const handleDragOver = useCallback((event) => {
    const { over } = event;
    if (!over) {
      setOverColumnId(null);
      return;
    }
    // over.id will be a column id since columns are the droppable targets
    const isColumn = COLUMNS.some(col => col.id === over.id);
    setOverColumnId(isColumn ? over.id : null);
  }, []);

  // Função para selecionar/deselecionar tarefas
  const handleSelectTask = useCallback((taskId, isMultiSelect) => {
    setSelectedTaskIds(prev => {
      const newSet = new Set(prev);
      if (isMultiSelect) {
        // Toggle selection
        if (newSet.has(taskId)) {
          newSet.delete(taskId);
        } else {
          newSet.add(taskId);
        }
      } else {
        // Single select - clear others
        if (newSet.has(taskId) && newSet.size === 1) {
          newSet.clear(); // Deselect if already selected alone
        } else {
          newSet.clear();
          newSet.add(taskId);
        }
      }
      return newSet;
    });
  }, []);

  const handleDragEnd = useCallback((event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverColumnId(null);

    if (!over) return;

    // over.id should be a column id
    const targetColumn = COLUMNS.find(col => col.id === over.id);
    if (!targetColumn) return;

    // Get all tasks to move (selected ones, or just the dragged one)
    const taskIdsToMove = selectedTaskIds.has(active.id) && selectedTaskIds.size > 1
      ? Array.from(selectedTaskIds)
      : [active.id];
    
    // Filter to only tasks that need to change status
    const tasksToUpdate = taskIdsToMove
      .map(id => filteredTasks.find(t => t.id === id))
      .filter(t => t && t.status !== targetColumn.id);

    if (tasksToUpdate.length === 0) return;

    // Update local state
    setTasks(prev => prev.map(t =>
      taskIdsToMove.includes(t.id) ? { ...t, status: targetColumn.id } : t
    ));

    // Update backend for each task
    tasksToUpdate.forEach(task => {
      apiService.updateTask(task.id, { status: targetColumn.id });
    });

    // Clear selection after move
    setSelectedTaskIds(new Set());

    // Toast message
    if (tasksToUpdate.length === 1) {
      toast.success(`Tarefa movida para ${targetColumn.label}`);
    } else {
      toast.success(`${tasksToUpdate.length} tarefas movidas para ${targetColumn.label}`);
    }
  }, [filteredTasks, selectedTaskIds]);

  const handleDragCancel = useCallback(() => {
    setActiveId(null);
    setOverColumnId(null);
  }, []);

  const handleTaskClick = (task) => {
    if (activeId) return; // Don't open dialog while dragging
    setSelectedTask(task);
    setFormData({
      title: task.title,
      desc: task.desc,
      status: task.status,
      priority: task.priority,
      risk_level: task.risk_level,
      owner_agent: task.owner_agent,
      project_id: task.project_id,
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedTask) {
        await apiService.updateTask(selectedTask.id, formData);
        toast.success('Tarefa atualizada');
      } else {
        await apiService.createTask(formData);
        toast.success('Tarefa criada');
      }
      setDialogOpen(false);
      setSelectedTask(null);
      setFormData({ title: '', desc: '', status: 'planejado', priority: 3, risk_level: 'medium', owner_agent: 'linus', project_id: selectedProject || (projects[0]?.id || null) });
      loadTasks();
    } catch (error) {
      toast.error('Erro ao salvar tarefa');
    }
  };

  const handleDelete = async () => {
    if (!selectedTask) return;
    
    if (!window.confirm(`Excluir tarefa "${selectedTask.title}"?`)) return;
    
    try {
      await apiService.deleteTask(selectedTask.id);
      toast.success('Tarefa excluída');
      setDialogOpen(false);
      setSelectedTask(null);
      loadTasks();
    } catch (error) {
      toast.error('Erro ao excluir tarefa');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-96">Carregando...</div>;
  }

  const activeTask = activeId ? filteredTasks.find(t => t.id === activeId) : null;

  return (
    <div data-testid="tasks-page">
      <PageHeader
        title="Tarefas"
        description="Kanban board com drag & drop"
        actions={
          <div className="flex flex-wrap items-center gap-2 sm:gap-3">
            {/* Filtro de Projetos - obrigatório */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground uppercase tracking-wider hidden sm:inline">
                Projeto:
              </span>
              <Select 
                value={selectedProject ? String(selectedProject) : ''} 
                onValueChange={(value) => setSelectedProject(parseInt(value))}
              >
                <SelectTrigger 
                  className="w-[160px] sm:w-[200px] h-9 text-xs"
                  data-testid="project-filter-select"
                >
                  <SelectValue>
                    {selectedProject 
                      ? projects.find(p => p.id === selectedProject)?.name || 'Carregando...'
                      : 'Selecione projeto'
                    }
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selection indicator */}
            {selectedTaskIds.size > 0 && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 border border-primary/30 rounded-md">
                <span className="text-xs text-primary font-medium">
                  {selectedTaskIds.size} selecionada{selectedTaskIds.size > 1 ? 's' : ''}
                </span>
                <button
                  onClick={() => setSelectedTaskIds(new Set())}
                  className="text-xs text-primary/70 hover:text-primary underline"
                >
                  Limpar
                </button>
              </div>
            )}

            <Button
              onClick={() => {
                setSelectedTask(null);
                setFormData({ 
                  title: '', 
                  desc: '', 
                  status: 'planejado', 
                  priority: 3, 
                  risk_level: 'medium', 
                  owner_agent: 'linus',
                  project_id: selectedProject || (projects[0]?.id || null)
                });
                setDialogOpen(true);
              }}
              className="bg-primary hover:bg-primary/90 rounded-sm uppercase text-xs h-9 px-3"
              data-testid="create-task-btn"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Nova</span>
            </Button>
          </div>
        }
      />

      <DndContext
        sensors={sensors}
        collisionDetection={rectIntersection}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {/* Kanban Board - Full Height */}
        <div className="flex flex-col md:grid md:grid-cols-3 gap-4" style={{ minHeight: 'calc(100vh - 220px)' }}>
          {COLUMNS.map((column) => {
            const columnTasks = filteredTasks.filter((t) => t.status === column.id);
            return (
              <DroppableColumn
                key={column.id}
                column={column}
                tasks={columnTasks}
                onTaskClick={handleTaskClick}
                isOver={overColumnId === column.id}
                selectedTaskIds={selectedTaskIds}
                onSelectTask={handleSelectTask}
              />
            );
          })}
        </div>

        <DragOverlay dropAnimation={{ duration: 200, easing: 'ease' }}>
          {activeTask ? <OverlayTaskCard task={activeTask} selectedCount={selectedTaskIds.size} /> : null}
        </DragOverlay>
      </DndContext>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-[#121215] border-border max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">
              {selectedTask ? 'Editar Tarefa' : 'Nova Tarefa'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="title" className="text-xs uppercase tracking-wider">Título</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                className="mt-1.5"
                data-testid="task-title-input"
              />
            </div>
            <div>
              <Label htmlFor="desc" className="text-xs uppercase tracking-wider">Descrição</Label>
              <Textarea
                id="desc"
                value={formData.desc}
                onChange={(e) => setFormData({ ...formData, desc: e.target.value })}
                className="mt-1.5"
                rows={3}
                data-testid="task-desc-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="status" className="text-xs uppercase tracking-wider">Status</Label>
                <Select value={formData.status} onValueChange={(value) => setFormData({ ...formData, status: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="planejado">Planejado</SelectItem>
                    <SelectItem value="aprovado">Aprovado</SelectItem>
                    <SelectItem value="concluido">Concluído</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="priority" className="text-xs uppercase tracking-wider">Prioridade</Label>
                <Select value={String(formData.priority)} onValueChange={(value) => setFormData({ ...formData, priority: parseInt(value) })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">P1 - Crítica</SelectItem>
                    <SelectItem value="2">P2 - Alta</SelectItem>
                    <SelectItem value="3">P3 - Média</SelectItem>
                    <SelectItem value="4">P4 - Baixa</SelectItem>
                    <SelectItem value="5">P5 - Mínima</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="risk_level" className="text-xs uppercase tracking-wider">Nível de Risco</Label>
                <Select value={formData.risk_level} onValueChange={(value) => setFormData({ ...formData, risk_level: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-risk-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">Baixo</SelectItem>
                    <SelectItem value="medium">Médio</SelectItem>
                    <SelectItem value="high">Alto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="owner_agent" className="text-xs uppercase tracking-wider">Agente</Label>
                <Select value={formData.owner_agent} onValueChange={(value) => setFormData({ ...formData, owner_agent: value })}>
                  <SelectTrigger className="mt-1.5" data-testid="task-agent-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Tom</SelectItem>
                    <SelectItem value="linus">Linus</SelectItem>
                    <SelectItem value="atlas">Atlas</SelectItem>
                    <SelectItem value="sys">SYS</SelectItem>
                    <SelectItem value="fred">Fred</SelectItem>
                    <SelectItem value="mark">Mark</SelectItem>
                    <SelectItem value="iris">Íris</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {/* Projeto - obrigatório para novas tarefas */}
            <div>
              <Label htmlFor="project_id" className="text-xs uppercase tracking-wider">Projeto *</Label>
              <Select 
                value={formData.project_id ? String(formData.project_id) : ''} 
                onValueChange={(value) => setFormData({ ...formData, project_id: parseInt(value) })}
              >
                <SelectTrigger className="mt-1.5" data-testid="task-project-select">
                  <SelectValue placeholder="Selecione um projeto" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(project => (
                    <SelectItem key={project.id} value={String(project.id)}>
                      {project.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 justify-between pt-4 border-t border-border/30">
              {/* Delete button - only show when editing */}
              {selectedTask ? (
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleDelete}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10 uppercase text-xs"
                  data-testid="delete-task-btn"
                >
                  <Trash2 className="h-4 w-4 mr-1.5" />
                  Excluir
                </Button>
              ) : (
                <div /> // Spacer
              )}
              
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDialogOpen(false);
                    setSelectedTask(null);
                  }}
                  className="uppercase text-xs"
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="bg-primary hover:bg-primary/90 uppercase text-xs"
                  data-testid="submit-task-btn"
                >
                  {selectedTask ? 'Atualizar' : 'Criar'}
                </Button>
              </div>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
